import useAuth from '../../Users/hooks/useAuth';
import { useState } from "react";

const UploadText = ({ book, successHandler, errorHandler, isUploading = false }) => {
    const [text, setText] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState(false);
   
    function texthandleChange(event) {
        setText(event.target.value);
        // Clear errors when user starts typing
        if (submitError) setSubmitError("");
        if (submitSuccess) setSubmitSuccess(false);
    }

    function eraseText() {
        setText("");
        setSubmitSuccess(false);
        setSubmitError("");
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // Prevent multiple submissions
        if (isSubmitting || isUploading) return;
        
        // Validate input
        if (!text || text.trim() === "") {
            setSubmitError("Please enter some text before saving.");
            return;
        }

        if (text.length < 10) {
            setSubmitError("Text seems too short. Please enter at least 10 characters.");
            return;
        }

        setIsSubmitting(true);
        setSubmitError("");
        setSubmitSuccess(false);

        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/upload-text`;
        const outdata = {
            text: text.trim(),
            title: book.title,
            language: book.language
        };
        
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(outdata),
            });
            
            if (response.ok) {
                setSubmitSuccess(true);
                eraseText();
                successHandler(true);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Upload failed with status: ${response.status}`);
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setSubmitError(error.message || "Failed to save text. Please try again.");
            errorHandler?.(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isDisabled = isSubmitting || isUploading || !text || text.trim() === "";

    return (
        <div className='flex flex-col p-6 bg-white rounded-lg border border-gray-200 shadow-sm'>
            <form className="flex flex-col mx-auto w-full" onSubmit={handleSubmit}>
                {/* Text Area */}
                <div className='flex flex-col mb-4 w-full'>
                    <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
                        Enter your presentation text
                    </label>
                    <textarea 
                        id="text-input"
                        className="border border-gray-300 rounded-lg p-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[200px] w-full"
                        rows={8}
                        placeholder="Paste or type your presentation text here..."
                        value={text}
                        onChange={texthandleChange}
                        disabled={isSubmitting || isUploading}
                    />
                    <div className="flex justify-between items-center mt-2">
                        <span className={`text-sm ${text.length < 10 ? 'text-red-500' : 'text-gray-500'}`}>
                            {text.length} characters
                        </span>
                        {text.length > 0 && (
                            <button
                                type="button"
                                onClick={eraseText}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                                disabled={isSubmitting || isUploading}
                            >
                                Clear text
                            </button>
                        )}
                    </div>
                </div>

                {/* Status Messages */}
                {submitError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-600 text-sm">{submitError}</p>
                    </div>
                )}

                {submitSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-600 text-sm">Text saved successfully!</p>
                    </div>
                )}

                {/* Submit Button */}
                <button 
                    className={`px-6 py-3 rounded-lg font-medium transition-colors w-full md:w-auto ${
                        isDisabled 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                    }`}
                    type="submit"
                    disabled={isDisabled}
                >
                    {isSubmitting ? (
                        <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Saving...
                        </div>
                    ) : isUploading ? (
                        "Please wait..."
                    ) : (
                        "Save Text"
                    )}
                </button>

                {/* Loading Indicator */}
                {(isSubmitting || isUploading) && (
                    <div className="mt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                            Processing your text...
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default UploadText;